// ============================================================
// VISION ENGINE — Fase 9.1
// Orquestrador central da pipeline de visão computacional
// Heurístico: sem ML pesado, pronto para upgrade futuro
// ============================================================
import { supabase } from '@/lib/supabase'
import { publishVisionEvent, createVisionEvent } from './visionEventBus'
import type { CameraDevice, VisionEvent } from '@/types/vision'
import { useVisionStore } from '@/store/visionStore'
import { obs } from '@/core/observability'

export interface VisionEngineConfig {
  obraId:           string
  companyId:        string
  snapshotInterval: number   // ms — default 5 min
  motionInterval:   number   // ms — default 2s per active camera
  maxConcurrent:    number   // max simultaneous streams
}

const DEFAULT_CONFIG: Omit<VisionEngineConfig, 'obraId' | 'companyId'> = {
  snapshotInterval: 300_000,   // 5 min
  motionInterval:   2_000,
  maxConcurrent:    6,
}

// ─── Engine state ─────────────────────────────────────────────
interface EngineState {
  timers:   ReturnType<typeof setInterval>[]
  channels: ReturnType<typeof supabase.channel>[]
  running:  boolean
}

const engines: Map<string, EngineState> = new Map()

// ─── Bootstrap vision engine for obra ─────────────────────────
export async function startVisionEngine(
  cfg: Partial<VisionEngineConfig> & { obraId: string; companyId: string },
): Promise<() => void> {
  const config: VisionEngineConfig = { ...DEFAULT_CONFIG, ...cfg }
  const { obraId, companyId } = config

  // Stop existing engine for this obra
  stopVisionEngine(obraId)

  const state: EngineState = { timers: [], channels: [], running: true }
  engines.set(obraId, state)

  obs.info('vision_engine', `Starting vision engine for obra ${obraId}`)

  // Load cameras
  await refreshCameras(obraId)

  // Subscribe to realtime vision_events
  const channel = supabase
    .channel(`vision-${obraId}`)
    .on('postgres_changes' as any, {
      event: 'INSERT', schema: 'public', table: 'vision_events',
      filter: `obra_id=eq.${obraId}`,
    }, (payload: any) => {
      const event = payload.new as VisionEvent
      if (event) {
        useVisionStore.getState().addEvent(event)
        publishVisionEvent(event)
      }
    })
    .on('postgres_changes' as any, {
      event: 'INSERT', schema: 'public', table: 'ppe_violations',
      filter: `obra_id=eq.${obraId}`,
    }, (payload: any) => {
      if (payload.new) useVisionStore.getState().addViolation(payload.new)
    })
    .subscribe()

  state.channels.push(channel)

  // Camera heartbeat check timer
  const heartbeatTimer = setInterval(() => {
    void checkCameraHeartbeats(obraId)
  }, 30_000) as unknown as ReturnType<typeof setInterval>
  state.timers.push(heartbeatTimer)

  // Snapshot scheduler timer
  const snapshotTimer = setInterval(() => {
    void triggerScheduledSnapshots(obraId)
  }, config.snapshotInterval) as unknown as ReturnType<typeof setInterval>
  state.timers.push(snapshotTimer)

  return () => stopVisionEngine(obraId)
}

export function stopVisionEngine(obraId: string): void {
  const state = engines.get(obraId)
  if (!state) return
  state.running = false
  state.timers.forEach(t => clearInterval(t))
  state.channels.forEach(c => c.unsubscribe())
  engines.delete(obraId)
}

// ─── Camera management ────────────────────────────────────────
export async function refreshCameras(obraId: string): Promise<void> {
  const { data, error } = await supabase
    .from('camera_devices')
    .select('*')
    .eq('obra_id', obraId)
    .eq('is_active', true)

  if (error) { obs.warn('vision_engine', `Camera load error: ${error.message}`); return }
  if (data) useVisionStore.getState().setCameras(data as CameraDevice[])
}

// ─── Heartbeat check ──────────────────────────────────────────
async function checkCameraHeartbeats(obraId: string): Promise<void> {
  const cameras = useVisionStore.getState().cameras.filter(c => c.obra_id === obraId)
  const now = Date.now()
  const OFFLINE_THRESHOLD = 60_000  // 60s without heartbeat → offline

  for (const cam of cameras) {
    const lastTs = cam.last_heartbeat ? new Date(cam.last_heartbeat).getTime() : 0
    const isStale = now - lastTs > OFFLINE_THRESHOLD
    const expectedStatus: CameraDevice['status'] = isStale ? 'offline' : 'online'

    if (cam.status !== expectedStatus) {
      useVisionStore.getState().updateCameraStatus(cam.id, expectedStatus)

      if (expectedStatus === 'offline' && cam.status === 'online') {
        // Camera went offline — publish event
        const evt = createVisionEvent(
          obraId, 'visual_risk', 'warning',
          `Câmera offline: ${cam.nome}`,
          { camera_id: cam.id, detail: `Sem heartbeat há ${Math.round((now - lastTs) / 1000)}s` },
        )
        publishVisionEvent(evt)
      }
    }
  }
}

// ─── Snapshot trigger ─────────────────────────────────────────
async function triggerScheduledSnapshots(obraId: string): Promise<void> {
  const cameras = useVisionStore.getState().cameras.filter(
    c => c.obra_id === obraId && c.status === 'online',
  )

  for (const cam of cameras) {
    if (!cam.snapshot_url) continue
    // In production: call edge function or backend to capture snapshot
    // Here: record a placeholder snapshot record
    const { error } = await supabase.from('camera_snapshots').insert({
      camera_id:     cam.id,
      obra_id:       obraId,
      storage_path:  `${obraId}/${cam.id}/${Date.now()}.jpg`,
      trigger:       'scheduled',
      analysis_done: false,
    })
    if (error) obs.warn('vision_engine', `Snapshot insert error: ${error.message}`)
  }
}

// ─── Publish mock event (dev/demo) ───────────────────────────
export function publishDemoEvent(obraId: string, tipo: VisionEvent['tipo']): void {
  const demos: Record<string, Pick<VisionEvent, 'title' | 'severity' | 'detail'>> = {
    ppe_violation:       { title: '🦺 EPI ausente detectado',         severity: 'critical', detail: 'Capacete não identificado na área de concretagem' },
    motion_detected:     { title: '⚡ Movimento detectado',            severity: 'info',     detail: 'Atividade operacional em área de armazenamento' },
    inactivity_detected: { title: '⏸ Área sem atividade',             severity: 'warning',  detail: 'Setor norte parado há 2 horas' },
    congestion_detected: { title: '🔴 Congestionamento detectado',     severity: 'warning',  detail: 'Acúmulo de operários próximo à grua' },
    visual_progress:     { title: '📈 Avanço visual registrado',       severity: 'info',     detail: 'Estrutura do 3º pavimento em andamento' },
    visual_risk:         { title: '⚠ Risco visual identificado',       severity: 'critical', detail: 'Obstrução detectada na rota de evacuação' },
    workforce_density:   { title: '👷 Alta concentração de efetivo',   severity: 'warning',  detail: 'Densidade acima do permitido na zona 4' },
    drone_scan_complete: { title: '🚁 Varredura de drone concluída',   severity: 'info',     detail: 'Ortofoto atualizada — área coberta: 3.200m²' },
  }

  const d = demos[tipo]
  if (!d) return

  const evt = createVisionEvent(obraId, tipo, d.severity, d.title, { detail: d.detail })
  publishVisionEvent(evt)
  useVisionStore.getState().addEvent(evt)
}
